import { bind } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import { encode } from 'base64-arraybuffer'
import { catchError, from, map, Observable, startWith, switchMap } from 'rxjs'
import { fromFetch } from 'rxjs/fetch'

import { CyberSourceFlexKeyMutation } from '../../graphql/magento'
import { coerceCaughtToLeft } from '../../utils/rx/errors'
import { mapRight, switchMapRight } from '../../utils/rx/operators'
import { fetchResponseToJsonResult, FetchResult, FetchRight, headers } from '../fetch'
import { GraphQLResult } from '../graphql'
import { currentFlexKey$ } from './flex-key'

export interface FlexApiKey extends JsonWebKey {
  kid: string
}

export interface FlexApiTokenizeInput {
  cardNumber: string
  cardType: string
  cardExpirationMonth: string
  cardExpirationYear: string
}

export interface FlexApiTokenizePostBody {
  keyId: string
  cardInfo: FlexApiTokenizeInput
}

export interface FlexApiTokenizeResponse {
  /** Unique identifier for the generated token which is obtained from the Generate Key request. */
  keyId: string
  /** The generated token. The token replaces card data and is used as the Subscription ID in the CyberSource Simple Order API or SCMP API. */
  token: string
  /** The masked card number displaying the first 6 digits and the last 4 digits. */
  maskedPan: string
  /** The card type. */
  cardType: string
  /** The UTC date and time in milliseconds at which the signature was generated. */
  timestamp: number
  /** Indicates which fields from the response make up the data that is used when verifying the response signature. */
  signedFields: string
  /** Flex-generated digital signature. To ensure the values have not been tampered with while passing through the client, verify the signature server-side using the public key generated from the /keys resource. */
  signature: string
}

const isFlexApiTokenizeResponse = (x: unknown): x is FlexApiTokenizeResponse => {
  if (typeof x !== 'object' || x == null) {
    return false
  }
  const y = x as Record<keyof FlexApiTokenizeResponse, unknown>
  return (
    typeof y.keyId === 'string' &&
    typeof y.token === 'string' &&
    typeof y.maskedPan === 'string' &&
    typeof y.cardType === 'string' &&
    typeof y.timestamp === 'number' &&
    typeof y.signedFields === 'string' &&
    typeof y.signature === 'string'
  )
}

const [flexApiTokenizeInput$, setFlexApiTokenizeInput] = createSignal<FlexApiTokenizeInput>()

export { setFlexApiTokenizeInput }

const isFlexApiKey = (x: unknown): x is FlexApiKey => {
  if (typeof x !== 'object' || x == null) {
    return false
  }
  return typeof (x as Partial<FlexApiKey>).kid === 'string'
}

const flexKeyFromQueryResult = (
  flexKeyResult: GraphQLResult<CyberSourceFlexKeyMutation>,
): FetchResult<FlexApiKey> => {
  if (flexKeyResult.error) {
    return { _tag: 'Left' as const, error: flexKeyResult.error }
  }
  const keyStore = flexKeyResult.data?.generateFlexJsToken?.key_store
  if (!keyStore) {
    return { _tag: 'Left' as const, error: new Error(`Flex API key store not available`) }
  }
  try {
    const parsedKeyStore = JSON.parse(keyStore)
    return isFlexApiKey(parsedKeyStore)
      ? { _tag: 'Right' as const, data: parsedKeyStore }
      : {
          _tag: 'Left' as const,
          error: new Error(`Flex API key store invalid`),
        }
  } catch (err) {
    return {
      _tag: 'Left' as const,
      error: new Error(
        `Unable to parse Flex API key store: ${err instanceof Error ? err.message : `${err}`}`,
      ),
    }
  }
}

const keyDataForEdge = ({ use, ...rest }: JsonWebKey): JsonWebKey => rest

const arrayBufferFrom = (input: string): ArrayBuffer => {
  const buf = new ArrayBuffer(input.length)
  const view = new Uint8Array(buf)
  for (let i = 0, inputLength = input.length; i < inputLength; i++) {
    view[i] = input.charCodeAt(i)
  }
  return buf
}

const postBodyFromInputAndKey =
  ({ cardNumber: plainTextCardNumber, ...otherCardInfo }: FlexApiTokenizeInput) =>
  <T extends FetchResult<FlexApiKey>>({
    data: flexApiKey,
  }: Extract<T, { _tag: 'Right' }>): Observable<
    FetchResult<{ key: FlexApiKey; body: FlexApiTokenizePostBody }>
  > => {
    const algorithm: RsaHashedImportParams = { name: 'RSA-OAEP', hash: { name: 'SHA-256' } }
    return from(
      window.crypto.subtle.importKey(
        'jwk',
        /Edge/.test(window.navigator.userAgent) ? keyDataForEdge(flexApiKey) : flexApiKey,
        algorithm,
        false,
        ['encrypt'],
      ),
    ).pipe(
      switchMap((cryptoKey) =>
        window.crypto.subtle
          .encrypt(algorithm, cryptoKey, arrayBufferFrom(plainTextCardNumber))
          .then(encode),
      ),
      map(
        (encryptedCardNumber): FetchRight<{ key: FlexApiKey; body: FlexApiTokenizePostBody }> => ({
          _tag: 'Right',
          data: {
            key: flexApiKey,
            body: {
              cardInfo: {
                ...otherCardInfo,
                cardNumber: encryptedCardNumber,
              },
              keyId: flexApiKey.kid,
            },
          },
        }),
      ),
      catchError(coerceCaughtToLeft),
    )
  }

const apiPath = '/cybersource/flex/v1/tokens'
const testBaseUrl = 'https://testflex.cybersource.com'
const productionBaseUrl = 'https://flex.cybersource.com'
const productionIndiaBaseUrl = 'https://flex.in.cybersource.com'

const tokenUrlFromKeyId = (keyId: string): string =>
  /^(?:01|02|03|04)/.test(keyId)
    ? `${productionBaseUrl}${apiPath}`
    : /^(?:09|10)/.test(keyId)
      ? `${productionIndiaBaseUrl}${apiPath}`
      : `${testBaseUrl}${apiPath}`

export const cyberSourceFlexApiTokenizeRequest = (
  tokenizeInput: FlexApiTokenizeInput,
): Observable<FetchResult<FlexApiTokenizeResponse>> =>
  currentFlexKey$.pipe(
    // TODO: troubleshoot bug where currentFlexKey$ produces undefined after getting an error from
    //  tokenize API
    map(flexKeyFromQueryResult),
    switchMapRight(postBodyFromInputAndKey(tokenizeInput)),
    switchMapRight(({ data: { key, body } }) =>
      fromFetch(tokenUrlFromKeyId(key.kid), {
        body: JSON.stringify(body),
        method: 'POST',
        headers: {
          ...headers.acceptJson,
          ...headers.contentTypeJson,
        },
      }).pipe(
        switchMap(fetchResponseToJsonResult),
        mapRight(({ data }) =>
          isFlexApiTokenizeResponse(data)
            ? { _tag: 'Right' as const, data }
            : {
                _tag: 'Left' as const,
                error: new Error(`Invalid response body: ${JSON.stringify(data)}`),
              },
        ),
        catchError(coerceCaughtToLeft),
      ),
    ),
  )

export const [useCyberSourceFlexApiTokenize, cyberSourceFlexApiTokenize$] = bind<{
  loading: boolean
  result: FetchResult<FlexApiTokenizeResponse> | undefined
}>(
  flexApiTokenizeInput$.pipe(
    switchMap(cyberSourceFlexApiTokenizeRequest),
    map((result) => ({
      loading: false,
      result,
    })),
    startWith({
      loading: true,
      result: undefined,
    }),
  ),
  {
    loading: false,
    result: undefined,
  },
)
